import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Database, CheckCircle, AlertCircle, Loader2, ChevronLeft } from 'lucide-react';

export default function TableMapping({ onComplete, onBack }) {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [mapping, setMapping] = useState({
    countries: '',
    continents: '',
    ports: '',
    regions: '',
    port_regions: '',
    time_zones: '',
    worklogs: '',
    eng_requests: '',
    eng_request_tasks: '',
    user_accounts: ''
  });

  const expectedTables = [
    { key: 'countries', label: 'Countries', required: true },
    { key: 'continents', label: 'Continents', required: true },
    { key: 'ports', label: 'Ports', required: true },
    { key: 'regions', label: 'Regions', required: true },
    { key: 'port_regions', label: 'Port-Regions', required: true },
    { key: 'time_zones', label: 'Time Zones', required: true },
    { key: 'worklogs', label: 'Worklogs', required: false },
    { key: 'eng_requests', label: 'Engineering Requests', required: false },
    { key: 'eng_request_tasks', label: 'Request Tasks', required: false },
    { key: 'user_accounts', label: 'User Accounts', required: false }
  ];

  useEffect(() => {
    fetchTables();
  }, []);

  const fetchTables = async () => {
    try {
      setLoading(true);
      const sessionId = sessionStorage.getItem('sessionId');
      
      const response = await fetch('/api/setup/tables', {
        headers: {
          'x-session-token': sessionId
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch tables');
      }

      const data = await response.json();
      setTables(data.tables);

      // Auto-detect matching table names
      const autoMapping = {};
      expectedTables.forEach(expected => {
        // Try exact match first
        const exactMatch = data.tables.find(t => t.name === expected.key);
        
        // Try singular form (remove 's' from end)
        const singularForm = expected.key.replace(/s$/, '');
        const singularMatch = data.tables.find(t => t.name === singularForm);
        
        // Try case-insensitive exact match
        const caseInsensitiveMatch = data.tables.find(t => 
          t.name.toLowerCase() === expected.key.toLowerCase()
        );
        
        // Try case-insensitive singular match
        const caseInsensitiveSingular = data.tables.find(t => 
          t.name.toLowerCase() === singularForm.toLowerCase()
        );
        
        // Only use similar match if it's very close (starts with the key)
        const similarMatch = data.tables.find(t => 
          t.name.toLowerCase().startsWith(expected.key.toLowerCase()) ||
          t.name.toLowerCase().startsWith(singularForm.toLowerCase())
        );
        
        // Prioritize: singular > exact > case-insensitive > similar
        autoMapping[expected.key] = (
          singularMatch || 
          caseInsensitiveSingular || 
          exactMatch || 
          caseInsensitiveMatch || 
          similarMatch
        )?.name || '';
      });

      setMapping(autoMapping);
    } catch (err) {
      console.error('Error fetching tables:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMappingChange = (key, value) => {
    setMapping(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const sessionId = sessionStorage.getItem('sessionId');

      const response = await fetch('/api/setup/table-mapping', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-session-token': sessionId
        },
        body: JSON.stringify({ mapping })
      });

      if (!response.ok) {
        throw new Error('Failed to save mapping');
      }

      // Store mapping in sessionStorage for frontend use
      sessionStorage.setItem('tableMapping', JSON.stringify(mapping));

      onComplete();
    } catch (err) {
      console.error('Error saving mapping:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const isValid = expectedTables
    .filter(t => t.required)
    .every(t => mapping[t.key]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <Card className="w-full max-w-3xl shadow-lg">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <span className="ml-3 text-gray-600">Loading database tables...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <Card className="w-full max-w-3xl shadow-lg">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-4">
            <Database className="h-12 w-12 text-blue-600" />
          </div>
          <CardTitle className="text-2xl text-center">Map Database Tables</CardTitle>
          <CardDescription className="text-center">
            Select which tables in your database correspond to each data type
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md mb-4">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-800">{error}</div>
            </div>
          )}

          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800">
              Found <strong>{tables.length}</strong> tables in your database. 
              {tables.length === 0 && " Your database appears to be empty. You may need to import your data first."}
            </p>
          </div>

          <div className="space-y-4 max-h-96 overflow-y-auto">
            {expectedTables.map(expected => {
              const selectedTable = tables.find(t => t.name === mapping[expected.key]);
              
              return (
                <div key={expected.key} className="flex items-start gap-3 p-3 border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <label className="font-medium text-sm">{expected.label}</label>
                      {expected.required && (
                        <Badge variant="destructive" className="text-xs">Required</Badge>
                      )}
                      {mapping[expected.key] && selectedTable && (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      )}
                    </div>
                    
                    <select
                      value={mapping[expected.key]}
                      onChange={(e) => handleMappingChange(expected.key, e.target.value)}
                      className="w-full p-2 border rounded-md text-sm"
                    >
                      <option value="">-- Select table --</option>
                      {tables.map(table => (
                        <option key={table.name} value={table.name}>
                          {table.name} ({table.rowCount} rows, {table.columns.length} columns)
                        </option>
                      ))}
                    </select>

                    {selectedTable && (
                      <div className="mt-2 text-xs text-gray-600">
                        <strong>Columns:</strong> {selectedTable.columns.map(c => c.name).join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 flex gap-3">
            {onBack && (
              <Button
                type="button"
                variant="outline"
                onClick={onBack}
                disabled={saving}
                className="gap-2"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </Button>
            )}
            <Button
              onClick={handleSave}
              disabled={!isValid || saving}
              className="flex-1"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Continue to Dashboard'
              )}
            </Button>
          </div>

          {!isValid && (
            <p className="mt-2 text-sm text-amber-600 text-center">
              Please map all required tables to continue
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
